import { DELIVERABLE_TYPES } from '../services/sergeApi'

/**
 * Vérifie si une valeur est un type de livrable valide
 * @param {string} value - Type de livrable à vérifier
 * @returns {boolean} true si valide
 */
export const isDeliverableType = (value) => {
  return Object.prototype.hasOwnProperty.call(DELIVERABLE_TYPES, value)
}

/**
 * Ordre recommandé de génération des livrables SERGE
 * L'ordre garantit que chaque livrable bénéficie du contexte des précédents
 * Phases: Analyse → Conception → Gestion → Légal
 * @type {string[]}
 */
export const RECOMMENDED_DELIVERABLE_ORDER = [
  // Ordre identique à Serge
  'spec_functional',
  'user_stories',
  'spec_technical',
  'diagram_class',
  'diagram_architecture',
  'diagram_sequence',
  'planning',
  'gantt_sheets',
  'market_study',
  'sales_strategy',
  'risk_register',
  'budget',
  'communication_plan',
  'technical_dossier',
  'gantt',
  'cgv',
  'cgu',
  'legal_notice',
  'privacy_policy',
  'software_licenses',
  'master_service_agreement',
  'payment_schedule',
  'demo',
]

/**
 * Filtre les types de livrables disponibles selon l'ordre recommandé
 * @param {Set<string>} available - Set des types de livrables disponibles
 * @returns {string[]} Types de livrables filtrés et triés
 */
export const getRecommendedDeliverables = (available) => {
  return RECOMMENDED_DELIVERABLE_ORDER.filter((type) => available.has(type))
}

/**
 * Obtient le rang d'un livrable dans l'ordre recommandé
 * @param {string} type - Type de livrable
 * @returns {number} Rang (0-based), 999 si non trouvé
 */
export const getRecommendedRank = (type) => {
  const index = RECOMMENDED_DELIVERABLE_ORDER.indexOf(type)
  return index === -1 ? 999 : index
}

/**
 * Trie les types de livrables selon l'ordre recommandé
 * @param {string[]} types - Types de livrables à trier
 * @returns {string[]} Types triés
 */
export const sortDeliverableTypes = (types) => {
  return [...types].sort((a, b) => getRecommendedRank(a) - getRecommendedRank(b))
}

/**
 * Obtient les prérequis manquants pour générer un livrable
 * Les prérequis sont tous les livrables recommandés AVANT le livrable cible
 * qui sont disponibles mais pas encore générés
 *
 * @param {string} target - Type de livrable cible
 * @param {Set<string>} generated - Set des types déjà générés
 * @param {Set<string>} available - Set des types disponibles
 * @returns {string[]} Types de livrables prérequis manquants
 */
export const getMissingRecommendedPrerequisites = (target, generated, available) => {
  const targetIndex = getRecommendedRank(target)
  if (targetIndex <= 0) return []

  // Tous les livrables recommandés AVANT le target
  const prereqs = RECOMMENDED_DELIVERABLE_ORDER.slice(0, targetIndex).filter((type) =>
    available.has(type)
  )

  // Filtrer ceux qui ne sont pas encore générés
  return prereqs.filter((type) => !generated.has(type))
}

/**
 * Estimations de temps de génération par type de livrable
 * @typedef {Object} DeliverableEstimate
 * @property {'range_minutes'|'not_implemented'} kind - Type d'estimation
 * @property {number} [min] - Temps minimum en minutes
 * @property {number} [max] - Temps maximum en minutes
 */

/**
 * Map des estimations par type de livrable
 * @type {Object.<string, DeliverableEstimate>}
 */
export const DELIVERABLE_ESTIMATES = {
  // Estimations identiques à Serge
  spec_functional: { kind: 'range_minutes', min: 15, max: 20 },
  user_stories: { kind: 'range_minutes', min: 5, max: 10 },
  spec_technical: { kind: 'range_minutes', min: 15, max: 20 },
  diagram_class: { kind: 'range_minutes', min: 1, max: 2 },
  diagram_architecture: { kind: 'range_minutes', min: 1, max: 2 },
  diagram_sequence: { kind: 'range_minutes', min: 1, max: 2 },
  planning: { kind: 'range_minutes', min: 3, max: 4 },
  gantt_sheets: { kind: 'range_minutes', min: 3, max: 4 },
  market_study: { kind: 'range_minutes', min: 5, max: 6 },
  sales_strategy: { kind: 'range_minutes', min: 3, max: 4 },
  risk_register: { kind: 'range_minutes', min: 3, max: 4 },
  budget: { kind: 'range_minutes', min: 3, max: 4 },
  communication_plan: { kind: 'range_minutes', min: 3, max: 4 },
  technical_dossier: { kind: 'not_implemented' },
  gantt: { kind: 'range_minutes', min: 1, max: 2 },
  cgv: { kind: 'range_minutes', min: 3, max: 5 },
  cgu: { kind: 'range_minutes', min: 3, max: 5 },
  legal_notice: { kind: 'range_minutes', min: 2, max: 3 },
  privacy_policy: { kind: 'range_minutes', min: 4, max: 6 },
  software_licenses: { kind: 'range_minutes', min: 2, max: 3 },
  master_service_agreement: { kind: 'range_minutes', min: 4, max: 7 },
  payment_schedule: { kind: 'range_minutes', min: 2, max: 3 },
  demo: { kind: 'range_minutes', min: 10, max: 15 },
}

/**
 * Obtient le label d'estimation pour un livrable
 * @param {string} type - Type de livrable
 * @returns {string|null} Label formaté (ex: "≈ 15–20 min") ou null
 */
export const getDeliverableEstimateLabel = (type) => {
  const estimate = DELIVERABLE_ESTIMATES[type]
  if (!estimate) return null
  if (estimate.kind === 'not_implemented') return 'Non implémenté'
  if (estimate.min === estimate.max) return `≈ ${estimate.min} min`
  return `≈ ${estimate.min}–${estimate.max} min`
}

/**
 * Vérifie si un livrable est implémenté
 * @param {string} type - Type de livrable
 * @returns {boolean} true si implémenté
 */
export const isDeliverableImplemented = (type) => {
  return DELIVERABLE_ESTIMATES[type]?.kind !== 'not_implemented'
}
